import { prepareContent } from '@/lib/message';
import { forwardRef, memo } from 'react';

import type { IMessageElement, IStep } from '@chainlit/react-client';

import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';
import { Markdown } from '@/components/Markdown';

import { InlinedElements } from './InlinedElements';
import { useTheme } from '@/components/ThemeProvider';

export interface Props {
  elements: IMessageElement[];
  message: IStep;
  allowHtml?: boolean;
  latex?: boolean;
}

const MessageContent = memo(
  forwardRef<HTMLDivElement, Props>(
    ({ message, elements, allowHtml, latex }, ref) => {
      const outputContent =
        message.streaming && message.output
          ? message.output + CURSOR_PLACEHOLDER
          : message.output;

      const {
        preparedContent: output,
        inlinedElements: outputInlinedElements,
        refElements: outputRefElements
      } = prepareContent({
        elements,
        id: message.id,
        content: outputContent,
        language: message.language
      });

      const displayInput = message.input && message.showInput;
      const theme = useTheme()
      const UserMSgStyleFixingLinkColor = theme.variant == "light" ? "" : ""
      const isMessage = message.type.includes('message');

      const outputMarkdown = (
        <>
          {!isMessage && displayInput && message.output ? (
            <div className="font-medium">Output</div>
          ) : null}
          <Markdown
            allowHtml={allowHtml}
            latex={latex}
            refElements={outputRefElements}
          >
            {output}
          </Markdown>
        </>
      );

      let inputMarkdown;

      if (displayInput) {
        const inputContent =
          message.streaming && message.input
            ? message.input + CURSOR_PLACEHOLDER
            : message.input;
        const { preparedContent: input, refElements: inputRefElements } =
          prepareContent({
            elements,
            id: message.id,
            content: inputContent,
            language:
              typeof message.showInput === 'string'
                ? message.showInput
                : undefined
          });

        inputMarkdown = (
          <>
            <div className="font-medium">Input</div>

            <Markdown
              allowHtml={allowHtml}
              latex={latex}
              refElements={inputRefElements}
              className={UserMSgStyleFixingLinkColor}
            >
              {input}
            </Markdown>
          </>
        );
      }

      const markdownContent = (
        <div className={`flex flex-col gap-4 ${UserMSgStyleFixingLinkColor}`}>
          {inputMarkdown}
          {outputMarkdown}
        </div>
      );

      return (
        <div ref={ref} className="message-content w-full flex flex-col gap-2">
          {!!inputMarkdown || output ? markdownContent : null}
          <InlinedElements elements={outputInlinedElements} />
        </div>
      );
    }
  )
);

export { MessageContent };
